/**
 * API Utility - Centralized API calls with CSRF protection
 *
 * Features:
 * - Automatic CSRF token inclusion in state-changing requests
 * - Cookie-based authentication (credentials: 'include')
 * - Automatic retry on CSRF token failure (403)
 * - Centralized error handling
 */

// Support both legacy REACT_APP_API_URL and current REACT_APP_API_BASE_URL.
// Trim trailing slash so endpoint joining is consistent.
const rawBaseUrl = process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');
const API_BASE_URL = rawBaseUrl.replace(/\/+$/, '');

// CSRF Token Management
const CSRF_TOKEN_KEY = 'csrfToken';

/**
 * Get CSRF token from localStorage
 * @returns {string|null} CSRF token or null if not found
 */
export function getCsrfToken() {
  return localStorage.getItem(CSRF_TOKEN_KEY);
}

/**
 * Set CSRF token in localStorage
 * @param {string} token - CSRF token to store
 */
export function setCsrfToken(token) {
  if (token) {
    localStorage.setItem(CSRF_TOKEN_KEY, token);
  }
}

/**
 * Clear CSRF token from localStorage
 */
export function clearCsrfToken() {
  localStorage.removeItem(CSRF_TOKEN_KEY);
}

/**
 * Refresh CSRF token from server
 * @returns {Promise<string>} New CSRF token
 * @throws {Error} If refresh fails
 */
async function refreshCsrfToken() {
  const response = await fetch(`${API_BASE_URL}/api/auth/csrf-token`, {
    method: 'GET',
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error('Failed to refresh CSRF token');
  }

  const data = await response.json();
  setCsrfToken(data.csrfToken);
  return data.csrfToken;
}

/**
 * Make API request with automatic CSRF token handling
 * @param {string} endpoint - API endpoint (e.g., '/api/auth/login')
 * @param {Object} options - Fetch options
 * @param {boolean} retry - Whether this is a retry attempt (internal use)
 * @returns {Promise<Response>} Fetch response
 */
async function apiRequest(endpoint, options = {}, retry = false) {
  // Ensure credentials are included for cookies
  const config = {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  };

  // Add CSRF token for state-changing methods
  const method = (options.method || 'GET').toUpperCase();
  const requiresCsrf = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);

  if (requiresCsrf) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }

  // Build full URL
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;

  // Make request
  let response = await fetch(url, config);

  // Handle CSRF token failure - refresh and retry once
  if (response.status === 403 && requiresCsrf && !retry) {
    try {
      // Refresh token
      const newToken = await refreshCsrfToken();

      // Retry request with new token
      config.headers['X-CSRF-Token'] = newToken;
      response = await fetch(url, config);
    } catch (error) {
      console.error('Failed to refresh CSRF token:', error);
      // Return original 403 response
    }
  }

  return response;
}

/**
 * Make GET request
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Additional fetch options
 * @returns {Promise<any>} Response data
 */
export async function get(endpoint, options = {}) {
  const response = await apiRequest(endpoint, {
    ...options,
    method: 'GET'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `GET ${endpoint} failed`);
  }

  return response.json();
}

/**
 * Make POST request
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request body data
 * @param {Object} options - Additional fetch options
 * @returns {Promise<any>} Response data
 */
export async function post(endpoint, data = null, options = {}) {
  const response = await apiRequest(endpoint, {
    ...options,
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `POST ${endpoint} failed`);
  }

  return response.json();
}

/**
 * Make PUT request
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request body data
 * @param {Object} options - Additional fetch options
 * @returns {Promise<any>} Response data
 */
export async function put(endpoint, data = null, options = {}) {
  const response = await apiRequest(endpoint, {
    ...options,
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `PUT ${endpoint} failed`);
  }

  return response.json();
}

/**
 * Make DELETE request
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Additional fetch options
 * @returns {Promise<any>} Response data
 */
export async function del(endpoint, options = {}) {
  const response = await apiRequest(endpoint, {
    ...options,
    method: 'DELETE'
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `DELETE ${endpoint} failed`);
  }

  return response.json();
}

/**
 * Make raw API request with full control (for custom use cases)
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} Raw fetch response
 */
export async function apiRequestRaw(endpoint, options = {}) {
  return apiRequest(endpoint, options);
}

// Export default object with all methods
const api = {
  get,
  post,
  put,
  del,
  delete: del, // Alias for delete
  raw: apiRequestRaw,
  getCsrfToken,
  setCsrfToken,
  clearCsrfToken
};

export default api;
